<template>
  <v-col class="pa-0">
    <!-- Compact Header Section with Inline Tabs (List View Only) -->
    <div
      v-if="showList"
      class="person-view-header-compact mb-3"
    >
      <v-toolbar
        flat
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Title Icon -->
        <div class="header-icon-wrapper mr-3 ml-3">
          <font-awesome-icon
            :icon="['fas', 'user']"
            size="lg"
            class="primary--text"
          />
        </div>

        <!-- Inline Tabs -->
        <v-tabs
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          show-arrows
          class="person-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-compact"
          >
            <font-awesome-icon
              :icon="tab.icon"
              class="mr-2"
              size="sm"
            />
            {{ tab.label }}
            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              :color="tab.chipColor || 'midgray'"
              class="ml-2 tab-count-chip"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-spacer />

        <!-- Action Buttons -->
        <div
          v-if="isUserInternalAdmin"
          class="header-actions d-flex align-center"
        >
          <!-- Add Person Button (Admin Only) -->
          <v-btn
            color="primary"
            depressed
            class="add-person-btn-compact mr-3"
            @click="addPerson"
          >
            <font-awesome-icon
              :icon="['fas', 'plus']"
              size="sm"
            />
            <span class="ml-2 d-none d-sm-inline">Add Person</span>
          </v-btn>
        </div>
      </v-toolbar>

      <v-divider />

      <!-- Compact Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="py-1"
          >
            <search-field
              v-model="searchText"
              placeholder="Search by name or email..."
            />
          </v-col>

          <!-- Data Tools (Filter, Group, Sort, Columns, Jump) -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <tools-toolbar
              :current-required-columns="requiredColumns"
              :report-key="reportKey"
              :table-columns="tableColumns"
              @on-apply-filters="onApplyFilters"
              @on-reset-filters="onResetFilters"
              @on-grouping-change="onGroupingChange"
              @on-sorting-change="onSortingChange"
            >
              <template v-slot:filter-controls>
                <person-filter-content
                  ref="personFilterPanel"
                  :selected-type-filter="selectedTypeFilter"
                  :selected-access-filter="selectedAccessFilter"
                  :selected-config-filter="selectedConfigFilter"
                  :selected-admin-filter="selectedAdminFilter"
                  :type-filter-options="typeFilterOptions"
                  :access-filter-options="accessFilterOptions"
                  :config-filter-options="configFilterOptions"
                  :admin-filter-options="adminFilterOptions"
                  @update:selectedTypeFilter="selectedTypeFilter = $event"
                  @update:selectedAccessFilter="selectedAccessFilter = $event"
                  @update:selectedConfigFilter="selectedConfigFilter = $event"
                  @update:selectedAdminFilter="selectedAdminFilter = $event"
                />
              </template>
            </tools-toolbar>
          </v-col>

          <!-- View Mode Dropdown -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-menu
              offset-y
              nudge-bottom="6"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="text-none"
                  v-bind="attrs"
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="viewMode === 'cards' ? ['fas', 'grip'] : ['fas', 'table']"
                    size="sm"
                    :class="{ 'mr-2': $vuetify.breakpoint.mdAndUp }"
                  />
                  <span class="d-none d-md-inline">{{ viewMode === 'cards' ? 'Cards' : 'Table' }}</span>
                  <font-awesome-icon
                    :icon="['fas', 'chevron-down']"
                    size="xs"
                    class="ml-2 d-none d-md-inline"
                  />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  :class="{ 'v-list-item--active primary--text': viewMode === 'cards' }"
                  @click="setViewMode('cards')"
                >
                  <v-list-item-icon class="mr-3">
                    <font-awesome-icon :icon="['fas', 'grip']" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Card View</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="viewMode === 'cards'">
                    <font-awesome-icon
                      :icon="['fas', 'check']"
                      class="primary--text"
                    />
                  </v-list-item-action>
                </v-list-item>
                <v-list-item
                  :class="{ 'v-list-item--active primary--text': viewMode === 'table' }"
                  @click="setViewMode('table')"
                >
                  <v-list-item-icon class="mr-3">
                    <font-awesome-icon :icon="['fas', 'table']" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Table View</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="viewMode === 'table'">
                    <font-awesome-icon
                      :icon="['fas', 'check']"
                      class="primary--text"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>

          <!-- Refresh Button -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-btn
              outlined
              class="text-none"
              :loading="isRefreshing"
              :disabled="isRefreshing || loading"
              @click="refreshData"
            >
              <font-awesome-icon
                :icon="['fas', 'arrows-rotate']"
                size="sm"
                :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
              />
              <span class="d-none d-md-inline">Refresh</span>
            </v-btn>
          </v-col>

          <v-spacer />

          <!-- Export Menu -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-menu
              offset-y
              nudge-bottom="6"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  depressed
                  class="text-none"
                  :disabled="!showExportBar"
                  v-bind="attrs"
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="['fas', 'download']"
                    size="sm"
                    :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                  />
                  <span class="d-none d-md-inline">Export</span>
                  <font-awesome-icon
                    :icon="['fas', 'chevron-down']"
                    size="xs"
                    class="ml-1 d-none d-md-inline"
                  />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="item in exportOptions"
                  :key="item.key"
                  :disabled="!item.active"
                  @click="item.callback"
                >
                  <v-list-item-title v-if="item.key !== 'xlsx'">
                    {{ item.text }}
                  </v-list-item-title>
                  <v-list-item-title v-else>
                    <export-xlsx
                      :item="item"
                      :show-export-bar="showExportBar"
                      :columns="exportColumns"
                      :groups="exportGroups"
                      :export-report-name="exportReportName"
                    />
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Jump to Group (contextual row) -->
    <jump-to-group
      :groups="exportGroups"
      class="ma-0 mb-1 ml-3"
      @scroll-to-group="scrollToGroup"
    />

    <!-- Person Profile View -->
    <person-profile
      v-if="showProfile"
      :person="selectedPerson"
    />

    <!-- Person List View -->
    <template v-if="showList">
      <!-- Quick Stats Banner (visible when filtering/searching) -->
      <v-row
        v-if="showQuickStats"
        class="ma-0 mb-3"
      >
        <v-col>
          <v-alert
            dense
            text
            color="primary"
            class="mb-0 quick-stats-alert"
          >
            <div class="d-flex align-center flex-wrap">
              <div class="d-flex align-center mr-4 mb-1">
                <font-awesome-icon
                  :icon="['fas', 'filter']"
                  class="mr-2"
                  size="sm"
                />
                <span class="font-weight-medium">
                  Showing {{ filteredPersons.length }} of {{ tabPersons.length }} {{ currentTab.label.toLowerCase() }} person{{ tabPersons.length === 1 ? '' : 's' }}
                </span>
              </div>
              <div
                v-if="searchText"
                class="d-flex align-center mb-1"
              >
                <font-awesome-icon
                  :icon="['fas', 'magnifying-glass']"
                  class="mr-2"
                  size="sm"
                />
                <span>Search: "{{ searchText }}"</span>
              </div>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row
        v-if="loading"
        class="ma-0"
      >
        <v-col
          v-for="n in 6"
          :key="`skeleton-${n}`"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-skeleton-loader
            type="card"
            elevation="1"
          />
        </v-col>
      </v-row>

      <!-- Cards View Mode -->
      <template v-if="viewMode === 'cards' && !loading">
        <!-- Person Cards - Grouped View -->
        <template v-if="filteredPersons.length > 0 && localGroupByProperties.length > 0">
          <div
            v-for="(personGroup, groupKey) in groupedPersons"
            :id="`group-${groupKey}`"
            :key="groupKey"
            class="mt-4"
          >
            <v-row class="mb-2">
              <v-col>
                <v-chip
                  color="primary"
                  label
                  outlined
                >
                  <font-awesome-icon
                    :icon="['fas', 'layer-group']"
                    size="sm"
                    class="mr-2"
                  />
                  {{ groupKey }}
                  <v-chip
                    x-small
                    class="ml-2"
                  >
                    {{ persons.length }}
                  </v-chip>
                </v-chip>
              </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col
                v-for="person in persons"
                :key="person.personId || person.email"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <person-card
                  :person="person"
                  :is-admin="isUserInternalAdmin"
                  @view-profile="viewPersonProfile"
                  @copy-person="copyPerson"
                  @edit="editPerson"
                  @delete="deletePerson"
                />
              </v-col>
            </v-row>
          </div>
        </template>

        <!-- Person Cards - No Grouping -->
        <v-row
          v-else-if="filteredPersons.length > 0"
          class="ma-0"
        >
          <v-col
            v-for="person in filteredPersons"
            :key="person.personId || person.email"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <person-card
              :person="person"
              :is-admin="isUserInternalAdmin"
              @view-profile="viewPersonProfile"
              @copy-person="copyPerson"
              @edit="editPerson"
              @delete="deletePerson"
            />
          </v-col>
        </v-row>

        <!-- Empty State - Cards View -->
        <v-row
          v-else
          class="ma-0"
        >
          <v-col>
            <v-card
              class="pa-8 text-center"
              elevation="0"
            >
              <font-awesome-icon
                :icon="['fas', 'user-slash']"
                size="3x"
                class="coolgray--text mb-4"
              />
              <div class="text-h6 coolgray--text">
                {{ emptyStateMessage }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Table View Mode -->
      <template v-else-if="viewMode === 'table' && !loading">
        <data-table
          v-if="filteredPersons.length > 0"
          ref="table"
          :actions="tableActions"
          :data-groups="currentDataGroups"
          :initial-items-per-page="25"
          :hide-toolbar-buttons="true"
        >
          <template v-slot:item.canAccessApps="{ value }">
            <font-awesome-icon
              v-if="value === true"
              :icon="['fas', 'check-circle']"
              class="bluegreen--text"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'times-circle']"
              class="toasted--text"
            />
          </template>

          <template v-slot:item.isConfigValid="{ value }">
            <font-awesome-icon
              v-if="value === true"
              :icon="['fas', 'check-circle']"
              class="bluegreen--text"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'exclamation-triangle']"
              class="toasted--text"
            />
          </template>

          <template v-slot:item.hasAccessErrors="{ value }">
            <span v-if="Array.isArray(value) && value.length > 0">
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="toasted--text mr-1"
              />
              {{ value.length }}
            </span>
            <span
              v-else
              class="midgray--text"
            >
              0
            </span>
          </template>

          <template v-slot:item.isPerson="{ value }">
            <font-awesome-icon
              v-if="value === true"
              :icon="['fas', 'check-circle']"
              class="bluegreen--text"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'times-circle']"
              class="toasted--text"
            />
          </template>

          <template v-slot:item.isPersonAuthorized="{ value }">
            <font-awesome-icon
              v-if="value === true"
              :icon="['fas', 'check-circle']"
              class="bluegreen--text"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'times-circle']"
              class="toasted--text"
            />
          </template>
        </data-table>

        <!-- Empty State - Table View -->
        <v-row
          v-else
          class="ma-0"
        >
          <v-col>
            <v-card
              class="pa-8 text-center"
              elevation="0"
            >
              <font-awesome-icon
                :icon="['fas', 'user-slash']"
                size="3x"
                class="coolgray--text mb-4"
              />
              <div class="text-h6 coolgray--text">
                {{ emptyStateMessage }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>

    <person-modal v-if="isUserInternalAdmin" />
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import _ from 'underscore'
import groupUtil from 'src/utils/group'
import api from '@/store/api.js'
import dates from '@/utils/dates.js'
import columns from '@/internal/store/utils/columns.js'
import tabQuerySync from '@/mixins/tabQuerySync.js'
import columnUtils from '@/tools/store/utils/columns.js'
import { personPath } from '@/utils/internalPaths'

const ExportXlsx = () => import('@/tools/components/export/ExportXlsx')
const PERSON_COLUMNS = columns.people
const DIAGNOSTIC_COLUMN_KEYS = ['canAccessApps', 'isConfigValid', 'hasAccessErrors']
const VALID_VIEW_MODES = ['cards', 'table']

const resolvePersonColumnGroup = (key) => {
  if (['fullName', 'email', 'personId', 'personNameFirst', 'personNameLast', 'isInternal', 'isActive', 'isAdmin', 'hasAccess', 'created_dt', 'updated_dt', 'isDeleted'].includes(key)) {
    return 'Profile'
  }

  if (['canAccessApps', 'isConfigValid', 'hasAccessErrors', 'hasAuthErrors', 'isPerson', 'isPersonAuthorized', 'isUser', 'isUserAuthorized', 'isUserActive'].includes(key)) {
    return 'Diagnostics'
  }

  if (['personRole', 'access.level', 'access.role', 'access.expires'].includes(key)) {
    return 'User Access'
  }

  if (['billingCodes', 'networkCodes', 'clientNames', 'networkTeamCodes', 'networkTeamIds', 'networkTeamNames', 'teamId', 'teamName', 'teamOfficeCode', 'teamOfficeName'].includes(key)) {
    return 'Organization'
  }

  if (['managerPersonId', 'managerPersonName'].includes(key)) {
    return 'Management'
  }

  if (key.startsWith('access.permissions.')) {
    return 'Permissions'
  }

  return 'Other'
}

const DEFAULT_VISIBLE_PERSON_COLUMNS = PERSON_COLUMNS
  .filter((column) => column.visible !== false)
  .map((column) => column.key)
  .concat(DIAGNOSTIC_COLUMN_KEYS)
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'PersonView',
  components: {
    DataTable: () => import('@/tools/components/table/DataTable'),
    ExportXlsx,
    PersonCard: () => import('@/internal/components/PersonCard'),
    PersonFilterContent: () => import('@/internal/components/PersonFilterContent'),
    PersonModal: () => import('@/internal/components/PersonModal'),
    PersonProfile: () => import('@/internal/components/Person'),
    ToolsToolbar: () => import('@/tools/components/filter/ToolsToolbar.vue'),
    JumpToGroup: () => import('@/tools/components/filter/JumpToGroup.vue'),
    SearchField: () => import('@/tools/components/filter/SearchField.vue'),
  },
  mixins: [tabQuerySync],
  data() {
    return {
      // Path utility for navigation
      personPath,
      // Tab query sync configuration
      tabQueryConfig: {
        queryParam: 'tab',
        validValues: ['all', 'internal', 'active', 'inactive', 'deleted'],
        defaultValue: 'all',
        additionalValidParams: ['search', 'personEmail', 'view', 'action'],
        disabledWhen: () => {
          // Disable tab query sync on profile pages (when reportId is present)
          const { reportId } = this.$route?.params || {}
          return !!reportId && reportId !== 'dashboard'
        },
      },

      persons: [],
      loading: false,
      isRefreshing: false,
      error: null,
      searchText: '',
      tabs: [
        { id: 'all', label: 'All', icon: ['fas', 'users'], chipColor: 'brightblue' },
        { id: 'internal', label: 'Internal', icon: ['fas', 'user-tie'], chipColor: 'primary' },
        { id: 'active', label: 'Active', icon: ['fas', 'user-check'], chipColor: 'bluegreen' },
        { id: 'inactive', label: 'Inactive', icon: ['fas', 'user-slash'], chipColor: 'coolgray' },
        { id: 'deleted', label: 'Deleted', icon: ['fas', 'trash'], chipColor: 'toasted' },
      ],
      // Filter state
      filterMenuOpen: false,
      selectedTypeFilter: null,
      selectedAccessFilter: null,
      selectedConfigFilter: null,
      selectedAdminFilter: null,
      typeFilterOptions: [
        { text: 'Internal', value: 'internal' },
        { text: 'External', value: 'external' },
      ],
      accessFilterOptions: [
        { text: 'Has Access', value: 'has-access' },
        { text: 'No Access', value: 'no-access' },
      ],
      configFilterOptions: [
        { text: 'Valid', value: 'valid' },
        { text: 'Invalid', value: 'invalid' },
      ],
      adminFilterOptions: [
        { text: 'Admin', value: 'admin' },
        { text: 'Non-Admin', value: 'non-admin' },
      ],
      // Grouping state
      localGroupByProperties: [],
      // Column visibility state (used for initial columns setup)
      visibleColumns: [...DEFAULT_VISIBLE_PERSON_COLUMNS],
      // View mode (persisted to localStorage)
      viewMode: 'cards', // 'cards' or 'table'
    }
  },
  computed: {
    ...mapAuthGetters(['isUserInternalAdmin']),
    ...mapToolsGetters([
      'currentGroupByProperties',
      'reportKey',
      'tableColumns',
    ]),
    ...mapToolsState({
      sortingByKeys: (state) => state.sortingByKeys || [],
      sortingByDirections: (state) => state.sortingByDirections || [],
    }),

    /**
     * Active tab index - syncs with URL query parameter
     */
    activeTab: {
      get() {
        return this.activeTabIndex
      },
      set(index) {
        this.setActiveTab(index)
      },
    },

    currentTab() {
      return this.tabs[this.activeTab] || this.tabs[0]
    },

    /**
     * ReportId from route params - represents the person's docId (email)
     */
    reportId() {
      return this.$route.params.reportId
    },

    /**
     * PersonEmail from query params - supports exact or partial email matching
     */
    personEmailQuery() {
      return this.$route.query.personEmail || ''
    },

    /**
     * Persons matching the personEmail query (exact or partial)
     */
    emailMatchedPersons() {
      if (!this.personEmailQuery) return []
      const queryLower = this.personEmailQuery.toLowerCase().trim()

      return this.persons.filter((p) => {
        const email = (p.email || '').toLowerCase()
        return email.includes(queryLower)
      })
    },

    /**
     * Show profile view when:
     * 1. reportId is present, OR
     * 2. personEmail query matches exactly one person
     */
    showProfile() {
      if (this.reportId && this.selectedPerson) return true
      if (this.personEmailQuery && this.emailMatchedPersons.length === 1) return true
      return false
    },

    /**
     * Show list view when not showing profile
     */
    showList() {
      return !this.showProfile
    },

    /**
     * Selected person for profile view - found by:
     * 1. reportId (route param), OR
     * 2. personEmail query (if exact match)
     */
    selectedPerson() {
      // Priority 1: Route-based selection
      if (this.reportId) {
        return this.persons.find((p) => p.docId === this.reportId || p.email === this.reportId)
      }

      // Priority 2: Query-based selection (if exactly one match)
      if (this.personEmailQuery && this.emailMatchedPersons.length === 1) {
        return this.emailMatchedPersons[0]
      }

      return null
    },

    tabPersons() {
      const { id } = this.currentTab
      switch (id) {
        case 'active':
          return this.persons.filter((p) => p.isActive && !p.isDeleted)
        case 'internal':
          return this.persons.filter((p) => p.isInternal && !p.isDeleted)
        case 'inactive':
          return this.persons.filter((p) => !p.isActive && !p.isDeleted)
        case 'deleted':
          return this.persons.filter((p) => p.isDeleted)
        default:
          return this.persons
      }
    },

    filteredPersons() {
      let filtered = this.tabPersons

      // Apply personEmail query filter (if multiple matches)
      if (this.personEmailQuery && this.emailMatchedPersons.length > 1) {
        filtered = this.emailMatchedPersons
      }

      // Apply search
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase()
        filtered = filtered.filter((person) => {
          const name = person.fullName || ''
          const email = person.email || ''
          return name.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower)
        })
      }

      // Apply type filter
      if (this.selectedTypeFilter) {
        filtered = filtered.filter((person) => {
          if (this.selectedTypeFilter === 'internal') return person.isInternal
          if (this.selectedTypeFilter === 'external') return !person.isInternal
          return true
        })
      }

      // Apply access filter
      if (this.selectedAccessFilter) {
        filtered = filtered.filter((person) => {
          if (this.selectedAccessFilter === 'has-access') return person.hasAccess
          if (this.selectedAccessFilter === 'no-access') return !person.hasAccess
          return true
        })
      }

      // Apply config filter
      if (this.selectedConfigFilter) {
        filtered = filtered.filter((person) => {
          if (this.selectedConfigFilter === 'valid') return person.isConfigValid
          if (this.selectedConfigFilter === 'invalid') return !person.isConfigValid
          return true
        })
      }

      // Apply admin filter
      if (this.selectedAdminFilter) {
        filtered = filtered.filter((person) => {
          if (this.selectedAdminFilter === 'admin') return person.isAdmin
          if (this.selectedAdminFilter === 'non-admin') return !person.isAdmin
          return true
        })
      }

      // Apply sorting from tools store
      if (this.sortingByKeys && this.sortingByKeys.length > 0) {
        filtered = this.applySorting([...filtered])
      }

      return filtered
    },

    showQuickStats() {
      // Show when actively filtering/searching
      return this.searchText && this.searchText.length > 0
    },

    hasActiveFilters() {
      return !!(
        this.selectedTypeFilter
        || this.selectedAccessFilter
        || this.selectedConfigFilter
        || this.selectedAdminFilter
      )
    },

    activeFilterCount() {
      let count = 0
      if (this.selectedTypeFilter) count += 1
      if (this.selectedAccessFilter) count += 1
      if (this.selectedConfigFilter) count += 1
      if (this.selectedAdminFilter) count += 1
      return count
    },

    emptyStateMessage() {
      if (this.searchText) {
        return `No persons found matching "${this.searchText}"`
      }
      return `No ${this.currentTab.label.toLowerCase()} persons found`
    },

    /**
     * Grouping options for persons
     */
    groupingOptions() {
      return [
        { text: 'Type', value: 'isInternal' },
        { text: 'Status', value: 'isActive' },
        { text: 'Admin', value: 'isAdmin' },
      ]
    },

    /**
     * Getter functions for group-by properties with boolean label transformations
     * Shared by groupedPersons (card view) and currentDataGroups (table view)
     */
    groupGetters() {
      return this.localGroupByProperties.map((prop) => {
        if (prop === 'isInternal') {
          return (item) => (item.isInternal ? 'Internal' : 'External')
        } if (prop === 'isActive') {
          return (item) => (item.isActive ? 'Active' : 'Inactive')
        } if (prop === 'isAdmin') {
          return (item) => (item.isAdmin ? 'Admin' : 'Non-Admin')
        }
        return (item) => item[prop] || 'Unassigned'
      })
    },

    /**
     * Grouped persons based on localGroupByProperties
     * Uses groupUtil.byProperty() with custom getter for boolean label transformations
     */
    groupedPersons() {
      if (this.localGroupByProperties.length === 0) {
        return {}
      }

      const groups = groupUtil.byProperty(this.filteredPersons, this.groupGetters)

      // Convert array format to object format for template iteration
      const groupsObject = {}
      groups.forEach((group) => {
        if (group.isPartOfGroup && group.groupName) {
          groupsObject[group.groupName] = group.items
        }
      })

      return groupsObject
    },

    personColumns() {
      const activeVisibleColumns = this.visibleColumns?.length
        ? this.visibleColumns
        : DEFAULT_VISIBLE_PERSON_COLUMNS

      return PERSON_COLUMNS.map((column, index) => ({
        ...column,
        group: resolvePersonColumnGroup(column.key),
        index,
        visible: activeVisibleColumns.includes(column.key),
      }))
    },

    /**
     * Available table columns for column picker
     */
    availableTableColumns() {
      return this.personColumns.map(({ columnName, key, required = false, visible = true }) => ({
        text: columnName,
        value: key,
        key,
        required,
        visible,
      }))
    },

    /**
     * Required columns that cannot be hidden
     */
    requiredColumns() {
      return this.availableTableColumns
        .filter((col) => col.required)
        .map((col) => col.value)
    },

    /**
     * Get visible column values from tableColumns store state
     */
    storeVisibleColumns() {
      if (!this.tableColumns || this.tableColumns.length === 0) {
        // Fallback to default visible columns if store not yet initialized
        return this.visibleColumns
      }
      return this.tableColumns
        .filter((col) => col.visible !== false)
        .map((col) => col.key)
    },

    /**
     * Show export bar when there's data to export
     */
    showExportBar() {
      return this.filteredPersons && this.filteredPersons.length > 0
    },

    /**
     * Export options for the dropdown menu
     */
    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },

    /**
     * Export columns configuration for persons
     */
    exportColumns() {
      return [
        {
          key: 'fullName',
          columnName: 'Name',
          formatAs: 'text',
          columnValue: (item) => item.fullName || '',
        },
        {
          key: 'email',
          columnName: 'Email',
          formatAs: 'text',
          columnValue: (item) => item.email || '',
        },
        {
          key: 'isInternal',
          columnName: 'Type',
          formatAs: 'text',
          columnValue: (item) => (item.isInternal ? 'Internal' : 'External'),
        },
        {
          key: 'isActive',
          columnName: 'Status',
          formatAs: 'text',
          columnValue: (item) => (item.isActive ? 'Active' : 'Inactive'),
        },
        {
          key: 'isAdmin',
          columnName: 'Admin',
          formatAs: 'text',
          columnValue: (item) => (item.isAdmin ? 'Yes' : 'No'),
        },
        {
          key: 'hasAccess',
          columnName: 'Has Access',
          formatAs: 'text',
          columnValue: (item) => (item.hasAccess ? 'Yes' : 'No'),
        },
      ]
    },

    /**
     * Export groups formatted for the tools/exportAs action
     */
    exportGroups() {
      if (this.localGroupByProperties.length > 0 && Object.keys(this.groupedPersons).length > 0) {
        return Object.entries(this.groupedPersons).map(([groupKey, items]) => ({
          key: groupKey,
          name: groupKey,
          groupName: groupKey,
          labels: [groupKey],
          items,
          rows: items,
        }))
      }

      // Default: single ungrouped list
      return [
        {
          key: 'all',
          name: 'All Persons',
          groupName: 'All Persons',
          labels: ['All Persons'],
          items: this.filteredPersons,
          rows: this.filteredPersons,
        },
      ]
    },

    /**
     * Export report name using current tab and date
     */
    exportReportName() {
      const tabLabel = this.currentTab?.label || 'Persons'
      const todayFormatted = dates.getFormattedDate(undefined, 'YYYY-MM-DD')

      return `persons_${tabLabel.replace(/\s+/g, '_').toLowerCase()}_${todayFormatted}`
    },

    /**
     * Data groups formatted for DataTable component
     */
    currentDataGroups() {
      const filteredItems = this.filteredPersons

      // Use same getter functions as groupedPersons for proper label transformations
      return groupUtil.byProperty(filteredItems, this.groupGetters)
    },

    /**
     * Actions available for DataTable rows
     */
    tableActions() {
      const actions = [
        {
          key: 'view',
          text: 'View Profile',
          icon: ['fas', 'eye'],
          callback: (item) => this.viewPersonProfile(item),
        },
        {
          key: 'edit',
          text: 'Edit',
          icon: ['fas', 'pen'],
          callback: (item) => this.editPerson(item),
          condition: () => this.isUserInternalAdmin,
        },
        {
          key: 'copy',
          text: 'Copy',
          icon: ['fas', 'copy'],
          callback: (item) => this.copyPerson(item),
          condition: () => this.isUserInternalAdmin,
        },
        {
          key: 'delete',
          text: 'Delete',
          icon: ['fas', 'trash'],
          callback: (item) => this.deletePerson(item),
          condition: () => this.isUserInternalAdmin,
        },
      ]

      // Filter out actions based on conditions
      return actions.filter((action) => !action.condition || action.condition())
    },
  },
  watch: {
    activeTab() {
      // Update tab counts when tab changes
      this.updateTabCounts()
    },
    reportId: {
      immediate: true,
      handler(newId) {
        // When navigating to a profile view, ensure data is loaded
        if (newId && this.persons.length === 0 && !this.loading) {
          this.fetchPersonsData()
        }
      },
    },
    personEmailQuery: {
      immediate: true,
      handler(newQuery) {
        // When personEmail query is present, ensure data is loaded
        if (newQuery && this.persons.length === 0 && !this.loading) {
          this.fetchPersonsData()
        }

        // Update search field to reflect the query
        if (newQuery && !this.searchText) {
          this.searchText = newQuery
        }
      },
    },

    '$route.query.view': {
      immediate: true,
      handler(newView) {
        if (VALID_VIEW_MODES.includes(newView) && newView !== this.viewMode) {
          this.viewMode = newView
        }
      },
    },

    '$route.query.action': {
      immediate: true,
      handler(action) {
        this.handleRouteAction(action)
      },
    },

    /**
     * Sync grouping from store to local state
     * This enables proper reactivity when grouping changes via ToolsDrawer
     */
    currentGroupByProperties: {
      immediate: true,
      handler(newGrouping) {
        if (newGrouping && Array.isArray(newGrouping)) {
          this.localGroupByProperties = [...newGrouping]
        }
      },
    },

    /**
     * Set selectedSearch when viewing a person profile via URL params
     * This ensures SearchBar displays the person's name instead of "selection not found"
     */
    selectedPerson: {
      immediate: true,
      handler(person) {
        // Only set selectedSearch when viewing a profile (reportId is present)
        if (!this.reportId || !person) return

        const selectedSearch = {
          isSearchOption: true,
          route: 'internal',
          reportKey: 'people',
          reportId: person.docId || person.email,
          reportName: 'people',
          text: person.fullName || person.personName || person.email,
          name: person.fullName || person.personName || person.email,
          email: person.email,
          dataType: 'person',
        }
        this.$store.commit('tools/selectedSearch', selectedSearch)
      },
    },
  },
  async mounted() {
    const storedViewMode = localStorage.getItem('personViewMode')
    const queryViewMode = this.$route.query.view

    if (VALID_VIEW_MODES.includes(queryViewMode)) {
      this.viewMode = queryViewMode
    } else if (VALID_VIEW_MODES.includes(storedViewMode)) {
      this.viewMode = storedViewMode
    }

    // Initialize tools store options for ToolsDrawer
    this.initializeToolsStoreOptions()

    // Load data on initial mount
    if (this.persons.length === 0) {
      await this.fetchPersonsData()
    }
  },
  methods: {
    setViewMode(mode) {
      if (!VALID_VIEW_MODES.includes(mode)) return

      this.viewMode = mode
      localStorage.setItem('personViewMode', mode)

      const query = {
        ...this.cleanTabQueryParameters(this.$route.query),
        view: mode,
      }

      if (this.$route.query.view !== mode) {
        this.$router.replace({ query }).catch((err) => {
          if (err?.name !== 'NavigationDuplicated') throw err
        })
      }
    },

    handleRouteAction(action) {
      if (action !== 'create' || !this.isUserInternalAdmin) return

      this.$store.dispatch('internal/showFormPersonCreate')

      const queryWithoutAction = { ...this.$route.query }
      delete queryWithoutAction.action
      this.$router.replace({ query: queryWithoutAction }).catch((err) => {
        if (err?.name !== 'NavigationDuplicated') throw err
      })
    },

    /**
     * Initialize grouping and sorting options in tools store.
     * This enables ToolsDrawer to work with Person view.
     */
    initializeToolsStoreOptions() {
      // Use the actual reportKey from tools store to ensure ToolsDrawer finds the columns
      const reportKey = this.reportKey || 'people'

      // Define grouping options for Person view
      const { groupingOptions } = this

      // Set grouping options in store
      this.$store.commit('tools/groupingOptions', { groupingOptions, reportKey })

      // Initialize empty grouping state if not already set
      if (!this.$store.state.tools.groupByProperties[reportKey]) {
        this.$store.commit('tools/grouping', { groupByProperties: [], reportKey })
      }

      const sortableColumns = this.personColumns.map((column) => ({ ...column }))
      const existingColumns = this.$store.state.tools.columns[reportKey]

      if (!columnUtils.hasCompatibleColumnSchema(existingColumns, sortableColumns)) {
        const storedPrefs = columnUtils.loadColumnPreferences(reportKey)
        const mergedColumns = columnUtils.applyColumnPreferences(sortableColumns, storedPrefs)
        this.$store.commit('tools/columns', { [reportKey]: mergedColumns })
      }

      // Initialize default sorting by name (ascending) if not set or if current sort key is invalid for this view
      const currentSortKeys = this.$store.state.tools.sortingByKeys || []
      const validKeys = sortableColumns.filter((c) => c.sortable).map((c) => c.key)
      const hasValidSortKey = currentSortKeys.length > 0 && currentSortKeys.every((key) => validKeys.includes(key))

      if (!hasValidSortKey) {
        this.$store.commit('tools/sortingByKeys', ['fullName'])
        this.$store.commit('tools/sortingByDirections', ['asc'])
      }
    },

    /**
     * Handle filter apply from ToolsDrawer
     * Filters are already applied via v-model binding, this is for any additional logic
     */
    onApplyFilters() {
      this.$log.info('[Person] Filters applied')
      // Filters are reactive, no additional action needed
    },

    /**
     * Handle filter reset from ToolsDrawer
     */
    onResetFilters() {
      const panel = this.$refs.personFilterPanel
      if (panel && panel.resetFilters) {
        panel.resetFilters()
      }
      // Also clear the local filter state
      this.clearAllFilters()
    },

    /**
     * Handle grouping changes from ToolsDrawer
     */
    onGroupingChange(groupByProperties) {
      this.localGroupByProperties = groupByProperties || []
    },

    /**
     * Handle sorting changes from ToolsDrawer
     * ToolsDrawer already commits to store, so we just need to trigger reactivity
     */
    onSortingChange(sorts) {
      // Sorting is handled by the store and applySorting computed
      // This handler allows views to react to sorting changes if needed
      this.$log.info('[Person] Sorting changed:', sorts)
    },

    /**
     * Apply sorting to an array of persons based on sortingByKeys and sortingByDirections.
     * Uses the underscore extension methods for case-insensitive sorting.
     * @param {Array} items - Array of persons to sort
     * @returns {Array} Sorted array
     */
    applySorting(items) {
      const { sortingByKeys, sortingByDirections } = this

      if (!sortingByKeys || sortingByKeys.length === 0) {
        return items
      }

      // Apply sorting in reverse order (last key first) to maintain proper multi-level sort
      const sortedItems = sortingByKeys.reduceRight((sorted, key, index) => {
        const direction = sortingByDirections[index] || 'asc'
        const sortDsc = direction === 'desc'

        const valueGetter = (item) => {
          const value = item[key]
          // Handle different value types
          if (typeof value === 'boolean') {
            return value ? 1 : 0
          }
          if (typeof value === 'string') {
            return value.toLowerCase()
          }
          return value ?? ''
        }

        const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
        return sorter(sorted, valueGetter)
      }, items)

      return sortedItems
    },

    /**
     * Toggle column visibility
     */
    onColumnVisibilityToggle(columnValue) {
      if (this.visibleColumns.includes(columnValue)) {
        this.visibleColumns = this.visibleColumns.filter((col) => col !== columnValue)
      } else {
        this.visibleColumns = [...this.visibleColumns, columnValue]
      }
    },

    /**
     * Reset columns to default visibility
     */
    resetColumns() {
      this.visibleColumns = [...DEFAULT_VISIBLE_PERSON_COLUMNS]
    },

    /**
     * Scroll to a specific group
     */
    scrollToGroup(groupKey) {
      this.$nextTick(() => {
        const element = document.getElementById(`group-${groupKey}`)
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      })
    },

    async fetchPersonsData() {
      this.loading = true
      this.error = null

      try {
        const response = await api.getPersons()
        const { content = [] } = response || {}

        const pickString = (...values) => {
          const first = values.find((value) => typeof value === 'string' && value.trim().length > 0)
          return first ? first.trim() : ''
        }

        const ensureList = (value) => (Array.isArray(value) ? value.filter((item) => item != null && `${item}`.trim() !== '') : [])

        const uniqueList = (value) => Array.from(new Set(ensureList(value)))

        const ensureListWithFallback = (value, fallback = 'N/A') => {
          const normalized = uniqueList(value)
          return normalized.length > 0 ? normalized : [fallback]
        }

        // Transform raw API data to include personName and other computed properties
        this.persons = content.map((person) => {
          const nameFirst = person.personNameFirst?.trim() || ''
          const nameLast = person.personNameLast?.trim() || ''
          const fullName = `${nameFirst} ${nameLast}`.trim() || 'Unknown'

          const toBoolean = (value) => {
            if (value === true || value === false) return value
            if (value === 'true') return true
            if (value === 'false') return false
            return undefined
          }

          const hasAccessErrors = Array.isArray(person.hasAccessErrors) ? person.hasAccessErrors : []
          const hasAuthErrors = Array.isArray(person.hasAuthErrors) ? person.hasAuthErrors : []
          const networks = Array.isArray(person.network) ? person.network : []
          const billingCodesFromNetwork = networks.map((network) => network?.organization?.code)
          const networkCodesFromNetwork = networks.map((network) => network?.organization?.networkCode)
          const clientNamesFromNetwork = networks.map((network) => network?.organization?.name)
          const networkTeamCodesFromNetwork = networks.flatMap((network) => network?.team?.map((team) => team?.code) || [])
          const networkTeamIdsFromNetwork = networks.flatMap((network) => network?.team?.map((team) => team?.id) || [])
          const networkTeamNamesFromNetwork = networks.flatMap((network) => network?.team?.map((team) => team?.name) || [])
          const accessLevel = pickString(person['access.level'], person?.access?.level)
          const accessRole = pickString(person['access.role'], person?.access?.role)
          const accessExpires = pickString(person['access.expires'], person?.access?.expires)
          const accessActive = toBoolean(person['access.active']) ?? toBoolean(person?.access?.active)
          const canAccessApps = toBoolean(person.canAccessApps) ?? toBoolean(person.hasAccess) ?? accessActive ?? false
          const hasPersonDocument = !!(person.personId || person.docId || person.personEmail || person.email)
          const isPerson = toBoolean(person.isPerson) ?? hasPersonDocument
          const isPersonAuthorized = toBoolean(person.isPersonAuthorized)
            ?? (isPerson && person.isDeleted !== true && person.isActive !== false)
          const isUser = toBoolean(person.isUser) ?? hasPersonDocument
          const isUserActive = toBoolean(person.isUserActive) ?? accessActive ?? false
          const isUserAuthorized = toBoolean(person.isUserAuthorized)
            ?? (isUser && isUserActive === true)

          return {
            ...person,
            personName: fullName,
            fullName,
            email: person.personEmail || '',
            canAccessApps,
            hasAccess: canAccessApps,
            isPerson,
            isPersonAuthorized,
            isUser,
            isUserActive,
            isUserAuthorized,
            isConfigValid: person.isConfigValid !== false,
            hasAccessErrors,
            hasAuthErrors,
            personRole: pickString(person.personRole, accessRole) || (person.isInternal ? 'internal' : 'external'),
            'access.active': isUserActive,
            'access.level': accessLevel || 'N/A',
            'access.role': accessRole || 'N/A',
            'access.expires': accessExpires || '2200-01-01T00:00:00',
            billingCodes: ensureListWithFallback(person.billingCodes || billingCodesFromNetwork),
            networkCodes: ensureListWithFallback(person.networkCodes || networkCodesFromNetwork),
            clientNames: ensureListWithFallback(person.clientNames || clientNamesFromNetwork),
            networkTeamCodes: ensureListWithFallback(person.networkTeamCodes || networkTeamCodesFromNetwork),
            networkTeamIds: ensureListWithFallback(person.networkTeamIds || networkTeamIdsFromNetwork),
            networkTeamNames: ensureListWithFallback(person.networkTeamNames || networkTeamNamesFromNetwork),
            teamId: pickString(person.teamId ? `${person.teamId}` : '', person.departmentId ? `${person.departmentId}` : '') || 'N/A',
            teamName: pickString(person.teamName, person.departmentName) || 'N/A',
            teamOfficeCode: pickString(person.teamOfficeCode, person.officeCode) || 'N/A',
            teamOfficeName: pickString(person.teamOfficeName, person.officeName) || 'N/A',
          }
        })

        this.updateTabCounts()
      } catch (error) {
        this.error = error.message || 'Failed to load persons data'
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: this.error,
          status: 'error',
        }, { root: true })
      } finally {
        this.loading = false
      }
    },

    /**
     * Refresh all data (called by Refresh button)
     */
    async refreshData() {
      this.isRefreshing = true
      try {
        await this.fetchPersonsData()
      } finally {
        this.isRefreshing = false
      }
    },

    updateTabCounts() {
      this.tabs = this.tabs.map((tab) => {
        let count = 0
        switch (tab.id) {
          case 'all':
            count = this.persons.length
            break
          case 'active':
            count = this.persons.filter((p) => p.isActive && !p.isDeleted).length
            break
          case 'internal':
            count = this.persons.filter((p) => p.isInternal && !p.isDeleted).length
            break
          case 'inactive':
            count = this.persons.filter((p) => !p.isActive && !p.isDeleted).length
            break
          case 'deleted':
            count = this.persons.filter((p) => p.isDeleted).length
            break
          default:
            count = 0
            break
        }
        return { ...tab, count }
      })
    },

    clearAllFilters() {
      this.selectedTypeFilter = null
      this.selectedAccessFilter = null
      this.selectedConfigFilter = null
      this.selectedAdminFilter = null
    },

    addPerson() {
      this.$store.dispatch('internal/showFormPersonCreate', undefined, { root: true })
    },

    viewPersonProfile(person) {
      if (person && person.email) {
        // Use query param navigation for better UX
        this.$router.push({
          path: this.personPath.list(),
          query: { personEmail: person.email },
        })
      } else if (person && person.docId) {
        // Fallback to route param if no email
        this.$router.push(this.personPath.profile(person.docId))
      }
    },

    copyPerson(person) {
      this.$store.dispatch('internal/showFormPersonCopy', person, { root: true })
    },

    editPerson(person) {
      this.$store.dispatch('internal/showFormPersonUpdate', person, { root: true })
    },

    deletePerson(person) {
      this.$store.dispatch('internal/showFormPersonDelete', person, { root: true })
    },

    /**
     * Export persons as CSV
     */
    exportAsCsv() {
      const exportData = {
        currentColumns: this.exportColumns,
        currentGroups: this.exportGroups,
        reportName: this.exportReportName,
        exportFileName: `${this.exportReportName}.csv`,
        exportType: 'csv',
      }

      this.$store.dispatch('tools/exportAs', exportData)
    },

    /**
     * Export persons as XLSX
     */
    exportAsXlsx() {
      const exportData = {
        currentColumns: this.exportColumns,
        currentGroups: this.exportGroups,
        reportName: this.exportReportName,
        exportFileName: `${this.exportReportName}.xlsx`,
        exportType: 'xlsx',
      }

      this.$store.dispatch('tools/exportAs', exportData)
    },
  },
}
</script>

<style scoped lang="scss">
.person-view-header-compact {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
}

.header-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background-color: white;
}

.header-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
}

.person-view-tabs-inline {
  background-color: white;

  ::v-deep .v-tab {
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0.01em;
    min-width: 120px;
  }
}

.tab-count-chip {
  font-size: 11px;
  font-weight: 600;
  height: 18px;
  min-width: 24px;
}

.controls-bar {
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.search-field-compact {
  ::v-deep .v-input__control {
    background-color: white;
  }
}

// Dark mode support
.theme--dark {
  .person-view-header-compact {
    background-color: #1e1e1e;
  }

  .header-toolbar {
    background-color: #1e1e1e;
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .person-view-tabs-inline {
    background-color: #1e1e1e;
  }

  .controls-bar {
    background-color: #272727;
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .search-field-compact {
    ::v-deep .v-input__control {
      background-color: #1e1e1e;
    }
  }
}

.search-icon {
  opacity: 0.6;
}

.quick-stats-alert {
  ::v-deep .v-alert__content {
    width: 100%;
  }
}
</style>
